/**
 * Sub-parts extracted from DriverDetailCard (565 lines → ~150 lines orchestrator).
 * Each part is a thin function-component focused on one section :
 *   - DriverHeader   : avatar + name + status + payroll
 *   - TraitsRow      : personality traits + daily mood delta
 *   - XpBar          : level + XP progress bar
 *   - GaugesRow      : fatigue + satisfaction gauges
 *   - ActionsRow     : rest / bonus / train / career / fire buttons
 *   - MissionHealth  : on-mission progress panel
 *   - CareerPanel    : level projection + spec suggestions (own file)
 *   - TrainingPanel  : catalog + buy buttons (own file)
 *   - ExpandedDetails: experience / risk / trainings history
 *
 * 29/05/2026 — Code review refactor.
 * 31/05/2026 — CareerPanel & TrainingPanel extracted into own files for
 *             further maintainability (parts.jsx 474 → ~305 lines).
 */
import React from 'react';
import { Gauge } from '../../game/Gauge';
import AvatarWithFallback from '../../AvatarWithFallback';
import CompetenceStars from '../CompetenceStars';
import PersonalityCursorPanel from '../PersonalityCursorPanel';
import { formatMoney, getInitials } from '../../../utils/format';
import { PERSONALITY_TRAITS, getTraitDailyDelta } from '../../../data/personalityTraits';
import { getDriverPhoto } from '../../../utils/driverPhoto';
import { getDriverSpecialties } from '../../../utils/driverSpecialties';
import { localize } from '../../../data/i18nData';
import {
  Coffee, Gift, AlertTriangle, Briefcase, GraduationCap,
  UserMinus, Award, Truck, ArrowUpRight, ArrowDownRight, Target, Sun, PiggyBank,
} from 'lucide-react';

// Re-export the two heavy panels from their own files so existing
// imports `from './driverDetail/parts'` keep working unchanged.
export { CareerPanel } from './CareerPanel';
export { TrainingPanel } from './TrainingPanel';

// Helper local — calcule la date de retour (DD/MM/YYYY) basée sur le
// calendrier interne du jeu (30 j/mois, 12 mois/an). Retourne null si
// l'une des entrées est absente. Exporté pour réutilisation par les
// autres composants HUD (Fleet, Maintenance).
export function computeReturnDate(day, month, year, daysFromNow) {
  if (day == null || month == null || year == null || daysFromNow == null) return null;
  let d = day + daysFromNow;
  let m = month;
  let y = year;
  while (d > 30) { d -= 30; m += 1; }
  while (m > 12) { m -= 12; y += 1; }
  return `${String(d).padStart(2, '0')}/${String(m).padStart(2, '0')}/${y}`;
}

export function DriverHeader({ driver, isTraining, trainingDaysLeft, t, calDay, calMonth, calYear, commercialOpp, totalDays }) {
  // 26/06/2026 — compteur dynamique : « Encore X j — retour JJ/MM/AAAA »
  // (recommandation user validée). On affiche la date dans le badge quand
  // c'est possible, sinon on retombe sur le compteur simple « XJ ».
  const returnDate = isTraining
    ? computeReturnDate(calDay, calMonth, calYear, trainingDaysLeft)
    : null;
  // 04/02/2026 — Q1 Bug Messagerie : statut distinct pour les missions
  // flash (opportunités commerciales acceptées via Messagerie). Le parent
  // (DriverDetailCard) calcule `daysLeft` à partir de totalDays et le
  // passe dans `commercialOpp.daysLeft`.
  const oppDaysLeft = commercialOpp?.daysLeft ?? 0;
  return (
    <>
      <div className={`w-11 h-11 rounded-full overflow-hidden flex items-center justify-center text-foreground text-sm font-bold flex-shrink-0 border-2 ${
        isTraining ? 'border-accent bg-accent/100' : commercialOpp ? 'border-purple-500 bg-purple-600' : driver.status === 'active' ? 'border-emerald-500 bg-emerald-600' : driver.status === 'resting' ? 'border-warn bg-warn/100' : 'border-accent bg-accent'
      }`}>
        <AvatarWithFallback
          src={getDriverPhoto(driver)}
          alt={driver.name}
          fallback={getInitials(driver.name)}
          className="w-full h-full"
          fallbackClassName="bg-ink-700 text-slate-300 text-xs font-bold"
        />
      </div>
      <div className="flex-1 min-w-0">
        <div className="flex items-center justify-between">
          <div>
            <div className="flex items-center gap-2">
              <h4 className="font-bold text-slate-100 text-sm">{driver.name}</h4>
              <span
                className={`text-[10px] px-1.5 py-0.5 rounded-full font-medium ${
                  isTraining ? 'bg-accent/15 text-accent' :
                  commercialOpp ? 'bg-purple-500/15 text-purple-300' :
                  driver.status === 'active' ? 'bg-money/10 text-money' :
                  driver.status === 'resting' ? 'bg-warn/10 text-warn' :
                  (driver.status === 'vacation' || driver.status === 'holiday' || driver.onHoliday) ? 'bg-purple-500/15 text-purple-300' :
                  driver.status === 'sick' ? 'bg-rose-500/15 text-rose-300' :
                  driver.status === 'striking' ? 'bg-amber-500/15 text-amber-300' :
                  driver.status === 'returning' ? 'bg-sky-500/15 text-sky-300' :
                  driver.assignedVehicleId ? 'bg-money/10 text-money' :
                  'bg-ink-700/40 text-slate-400'
                }`}
                data-testid={isTraining ? `training-countdown-${driver.id}` : `driver-status-${driver.id}`}
                title={isTraining && returnDate ? `Retour prévu le ${returnDate}`
                  : commercialOpp ? `Mission flash pour ${commercialOpp.clientName} — retour J+${oppDaysLeft}, prime ${(commercialOpp.treasuryReward || 0).toLocaleString('fr-FR')} €`
                  : undefined}
              >
                {isTraining
                  ? (returnDate
                      ? `Encore ${trainingDaysLeft}${t('training_days')} — retour ${returnDate}`
                      : `${t('training_label')} (${trainingDaysLeft}${t('training_days')})`)
                  : commercialOpp ? `Mission flash — J+${oppDaysLeft}`
                  : driver.status === 'active' ? t('active_hr_status')
                  : driver.status === 'resting' ? t('resting_status')
                  : (driver.status === 'vacation' || driver.status === 'holiday' || driver.onHoliday) ? t('holiday_hr_status')
                  : driver.status === 'sick' ? t('sick_hr_status')
                  : driver.status === 'striking' ? t('striking_hr_status')
                  : driver.status === 'returning' ? t('returning_hr_status')
                  // 12/02/2026 (fix user prod « voir des chauffeurs
                  // disponibles alors qu'en mission ») — anti-drift : si
                  // le chauffeur porte un véhicule assigné, on force
                  // « En mission » même si le status n'est pas encore
                  // synchronisé sur 'active'.
                  : driver.assignedVehicleId ? t('active_hr_status')
                  : t('available_hr_status')}
              </span>
            </div>
            <p className="text-xs text-slate-500 mt-0.5">
              Niv. {driver.level} — {getDriverSpecialties(driver).join(' / ') || '—'}
            </p>
            {driver.bioKey && t(driver.bioKey) && t(driver.bioKey) !== driver.bioKey && (
              <p className="text-[11px] text-slate-400 italic mt-1 leading-snug" data-testid={`driver-bio-${driver.id}`}>
                « {t(driver.bioKey)} »
                {driver.acquiredFromCompetitorId && (
                  <span className="text-[10px] text-amber-300/70 font-mono not-italic ml-1">· M&A</span>
                )}
              </p>
            )}
            {/* 09/02/2026 — Q2 Lot E user prod : badge VISIBLE pour les
                chauffeurs en préavis (refus de négociation salariale). Le
                bug remonté en prod : "chauffeurs disparaissent sans raison".
                Le préavis est posé dans messageSlice.driverCounterOffer
                refusé ; sans badge, le joueur ne voyait rien jusqu'à la
                disparition effective 30 j plus tard. */}
            {driver.pendingDeparture && driver.pendingDeparture.noticeUntil && (
              <div
                className="mt-1.5 inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full bg-rose-500/15 border border-rose-400/40"
                data-testid={`driver-pending-departure-${driver.id}`}
                title={`Préavis de départ : ${driver.pendingDeparture.expectedRaise ? `attendait +${driver.pendingDeparture.expectedRaise} €/mois. ` : ''}Vous pouvez encore aligner le salaire via la Messagerie.`}
              >
                <span className="w-1.5 h-1.5 rounded-full bg-rose-400 animate-pulse" />
                <span className="text-[10px] font-mono font-bold text-rose-200">
                  Préavis — départ J-{Math.max(0, (driver.pendingDeparture.noticeUntil || 0) - (totalDays || 0))}
                </span>
                <span className="text-[10px] text-rose-300/80 ml-1">Aligner via Messagerie</span>
              </div>
            )}
          </div>
          <div className="text-right flex-shrink-0">
            <span className="text-xs font-semibold text-danger">{formatMoney(driver.employerCost || 0)}/m</span>
            <p className="text-[10px] text-slate-500">{t('payroll')}</p>
          </div>
        </div>
      </div>
    </>
  );
}

export function TraitsRow({ driver, vehicles, activeContracts, hrInsightsUnlocked }) {
  if (!(driver.personalityTraits || []).length) return null;
  const linkedVehicle = vehicles.find(v => v.id === driver.assignedVehicleId);
  const linkedContract = (activeContracts || []).find(c => c.driverId === driver.id);
  const delta = hrInsightsUnlocked && driver.status === 'active'
    ? getTraitDailyDelta(driver, linkedVehicle, linkedContract)
    : 0;
  return (
    <div className="flex flex-wrap gap-1 mt-1 items-center" data-testid={`driver-traits-${driver.id}`}>
      {driver.personalityTraits.map(traitId => {
        const traitDef = PERSONALITY_TRAITS[traitId];
        if (!traitDef) return null;
        const { likes, dislikes } = traitDef.dailyImpact || {};
        let tooltip = traitDef.description;
        if (hrInsightsUnlocked) {
          if (likes) tooltip += `\n+ ${likes}`;
          if (dislikes) tooltip += `\n− ${dislikes}`;
        }
        return (
          <span
            key={traitId}
            className="text-[10px] px-1.5 py-0.5 rounded-full bg-accent/10 text-accent font-medium border border-accent/30 whitespace-pre-line"
            title={tooltip}
          >
            {traitDef.label}
          </span>
        );
      })}
      {delta !== 0 && (
        <span
          className={`inline-flex items-center gap-0.5 text-[10px] px-1.5 py-0.5 rounded-full font-mono font-bold border ${
            delta > 0 ? 'bg-money/10 text-money border-money/40' : 'bg-danger/10 text-danger border-danger/40'
          }`}
          data-testid={`trait-mood-${driver.id}`}
          title={delta > 0
            ? `Mission appréciée par ${driver.name} — +${delta.toFixed(2)} sat/j`
            : `Mission qui le/la déprime — ${delta.toFixed(2)} sat/j`}
        >
          {delta > 0 ? <ArrowUpRight className="w-2.5 h-2.5" /> : <ArrowDownRight className="w-2.5 h-2.5" />}
          {delta > 0 ? '+' : ''}{delta.toFixed(2)}/j
        </span>
      )}
    </div>
  );
}

export function XpBar({ driver }) {
  const XP_THRESHOLDS = [0, 100, 300, 600, 1000, 1500];
  const lvl = driver.level || 1;
  const prevThreshold = XP_THRESHOLDS[lvl - 1] ?? 0;
  const nextThreshold = driver.xpToNext || XP_THRESHOLDS[lvl] || (prevThreshold + 600);
  const span = Math.max(1, nextThreshold - prevThreshold);
  const inLevel = Math.max(0, (driver.xp || 0) - prevThreshold);
  const pct = Math.min(100, (inLevel / span) * 100);
  return (
    <div className="mt-2 flex items-center gap-2" data-testid={`xp-bar-${driver.id}`}>
      <span className="text-[10px] font-bold text-foreground w-8">Niv.{lvl}</span>
      <div className="flex-1 h-2 rounded-full bg-ink-700/40 overflow-hidden">
        <div
          className="h-full rounded-full bg-gradient-to-r from-blue-500 to-indigo-500 transition-all duration-500"
          style={{ width: `${pct}%` }}
        />
      </div>
      <span className="text-[10px] text-slate-500 w-20 text-right">{inLevel} / {span} XP</span>
    </div>
  );
}

export function GaugesRow({ driver, t }) {
  return (
    <div className="grid grid-cols-2 gap-3 mt-3">
      <div>
        <div className="flex items-center justify-between mb-0.5">
          <span className="text-[10px] text-slate-500">{t('fatigue')}</span>
          <span className="text-[10px] font-bold text-slate-500">{Math.round(driver.fatigue || 0)}%</span>
        </div>
        <Gauge value={driver.fatigue || 0} type="fatigue" />
      </div>
      <div>
        <div className="flex items-center justify-between mb-0.5">
          <span className="text-[10px] text-slate-500">Satisfaction</span>
          <span className="text-[10px] font-bold text-slate-500">{Math.round(driver.satisfaction || 0)}%</span>
        </div>
        <Gauge value={driver.satisfaction || 0} type="satisfaction" />
      </div>
    </div>
  );
}

export function ActionsRow({ driver, treasury, isTraining, isOnMission, trainingBlocked, onRest, onBonus, onSendOnHoliday, onBuyBackHolidays, setShowTraining, showTraining, setShowCareer, showCareer, onFire, t, calDay, calMonth, calYear, totalDays }) {
  const holidaysLeft = driver.holidayDaysRemaining || 0;
  const isOnHoliday = !!driver.onHoliday;
  // 17/07/2026 — cumul mensuel : détail « cumulés / pris / restants ».
  const accruedYear = driver.holidayDaysAccruedYear || 0;
  const takenYear = driver.holidayDaysTakenYear || 0;
  const fmtJ = (n) => (Number.isInteger(n) ? `${n}` : n.toLocaleString('fr-FR', { maximumFractionDigits: 1 }));
  // 26/06/2026 (recommandation user) — compteur dynamique de retour de
  // congés : « Encore Xj — retour JJ/MM/AAAA » au lieu de l'ancien
  // « En congés (Xj) ». Cohérent avec le compteur formation.
  const holidayDaysLeft = isOnHoliday && totalDays != null
    ? Math.max(0, (driver.holidayEndDay || 0) - totalDays)
    : 0;
  const holidayReturnDate = isOnHoliday
    ? computeReturnDate(calDay, calMonth, calYear, holidayDaysLeft)
    : null;
  // 13/06/2026 — CET : rachat de 5 jours de congés (plafond 10 j/an).
  const boughtThisYear = driver.holidayDaysBoughtThisYear || 0;
  const buyBackCost = Math.round(((driver.employerCost || driver.salaryGross || 2800) / 21.7) * 5 * 1.10);
  const canBuyBack = !isOnHoliday && holidaysLeft >= 5 && boughtThisYear + 5 <= 10 && treasury >= buyBackCost;
  return (
    <div className="flex flex-wrap gap-1.5 mt-3">
      <button onClick={() => onRest(driver.id)} disabled={driver.fatigue < 20 || isTraining || isOnHoliday}
        className="text-xs border border-ink-600 text-slate-400 px-2.5 py-1.5 rounded-md hover:bg-ink-700/40 disabled:opacity-40 transition-colors flex items-center gap-1"
        data-testid={`rest-${driver.id}`}>
        <Coffee className="w-3 h-3" /> {t('rest')}
      </button>
      <button onClick={() => onBonus(driver.id)} disabled={treasury < 150 || isTraining || isOnHoliday}
        className="text-xs border border-ink-600 text-slate-400 px-2.5 py-1.5 rounded-md hover:bg-ink-700/40 disabled:opacity-40 transition-colors flex items-center gap-1"
        data-testid={`bonus-${driver.id}`}>
        <Gift className="w-3 h-3" /> {t('bonus')}
      </button>
      {/* 17/06/2026 (Lot 4) — Gestion autonome des congés : la RH gère
          automatiquement les départs. Le bouton manuel est désactivé,
          on affiche désormais une pastille d'info passive (solde + statut). */}
      {onSendOnHoliday && (
        <span
          title={
            isOnHoliday
              ? (holidayReturnDate
                  ? `En congés — retour prévu le ${holidayReturnDate}`
                  : `En congés (retour J${driver.holidayEndDay ?? '?'})`)
              : holidaysLeft < 5
                ? `Solde insuffisant pour une demande (${fmtJ(holidaysLeft)} j / 5 requis). Cumul : +2,5 j au 1er de chaque mois (${fmtJ(accruedYear)} j cumulés · ${fmtJ(takenYear)} j pris cette année).`
                : (driver.holidayPressureLossThisYear || 0) > 0
                  ? `Pression congés en cours : déjà −${driver.holidayPressureLossThisYear} satisfaction cette année. La RH va déclencher un départ dès que possible.`
                  : `Cumul mensuel : +2,5 j au 1er de chaque mois (30 j/an). Cette année : ${fmtJ(accruedYear)} j cumulés · ${fmtJ(takenYear)} j pris · solde ${fmtJ(holidaysLeft)} j. La RH déclenche les départs au fil de l'eau (5 jours par bloc).`
          }
          className={`text-xs border px-2.5 py-1.5 rounded-md transition-colors flex items-center gap-1 relative cursor-default ${
            isOnHoliday
              ? 'border-emerald-500/40 text-emerald-300'
              : (driver.holidayPressureLossThisYear || 0) > 0
                ? 'border-rose-500/60 text-rose-300'
                : 'border-amber-500/40 text-amber-300'
          }`}
          data-testid={`holiday-${driver.id}`}
        >
          <Sun className="w-3 h-3" />
          {isOnHoliday
            ? (holidayReturnDate
                ? `Encore ${holidayDaysLeft}j — retour ${holidayReturnDate}`
                : `En congés (${Math.max(0, (driver.holidayEndDay ?? 0) - (driver.holidayStartDay ?? 0))}j)`)
            : (driver.holidayPressureLossThisYear || 0) > 0
              ? `Congés ${fmtJ(holidaysLeft)}j · −${driver.holidayPressureLossThisYear} sat`
              : `Congés ${fmtJ(holidaysLeft)}j (auto)`}
          {(driver.holidayPressureLossThisYear || 0) > 0 && !isOnHoliday && (
            <span
              className="absolute -top-1 -right-1 w-2 h-2 rounded-full bg-rose-400 animate-pulse"
              data-testid={`holiday-pressure-dot-${driver.id}`}
              aria-label="pression congés"
            />
          )}
        </span>
      )}
      {/* 13/06/2026 — CET : rachat de 5 jours de congés. Visible dès que le
          chauffeur a un solde ≥ 5 j, réduit la pression congés contre cash. */}
      {onBuyBackHolidays && holidaysLeft >= 5 && !isOnHoliday && (
        <button
          onClick={() => onBuyBackHolidays(driver.id)}
          disabled={!canBuyBack}
          title={
            boughtThisYear + 5 > 10
              ? `Plafond CET atteint (${boughtThisYear}/10 j rachetés cette année)`
              : treasury < buyBackCost
                ? `Trésorerie insuffisante (${formatMoney(buyBackCost)} requis)`
                : `Compte Épargne Temps : racheter 5 jours de congés pour ${formatMoney(buyBackCost)} (indemnité versée au chauffeur, satisfaction +3, pression congés réduite). Plafond : 10 j/an.`
          }
          className="text-xs border border-emerald-500/40 text-emerald-300 px-2.5 py-1.5 rounded-md hover:bg-emerald-500/10 disabled:opacity-40 disabled:cursor-not-allowed transition-colors flex items-center gap-1"
          data-testid={`holiday-buyback-${driver.id}`}
        >
          <PiggyBank className="w-3 h-3" />
          CET 5j · {formatMoney(buyBackCost)}
        </button>
      )}
      <button onClick={() => setShowTraining(!showTraining)} disabled={trainingBlocked || isOnHoliday}
        className="text-xs border border-accent/40 text-accent px-2.5 py-1.5 rounded-md hover:bg-accent/10 disabled:opacity-40 disabled:cursor-not-allowed transition-colors flex items-center gap-1"
        data-testid={`train-toggle-${driver.id}`} title={isOnMission ? t('cannot_train_on_mission') : undefined}>
        <GraduationCap className="w-3 h-3" /> {t('train')}
      </button>
      <button onClick={() => setShowCareer(!showCareer)}
        className="text-xs border border-money/40 text-money px-2.5 py-1.5 rounded-md hover:bg-money/10 transition-colors flex items-center gap-1"
        data-testid={`career-toggle-${driver.id}`} title="Plan de carrière">
        <Target className="w-3 h-3" /> Carrière
      </button>
      <button onClick={() => onFire(driver.id)} disabled={isTraining || isOnMission || isOnHoliday}
        title={isOnMission ? t('cannot_fire_on_mission') : (isOnHoliday ? 'Impossible de licencier en congés' : undefined)}
        className="text-xs border border-danger/40 text-danger px-2.5 py-1.5 rounded-md hover:bg-danger/10 disabled:opacity-40 disabled:cursor-not-allowed transition-colors ml-auto flex items-center gap-1"
        data-testid={`fire-${driver.id}`}>
        <UserMinus className="w-3 h-3" /> {t('fire')}
      </button>
    </div>
  );
}

export function MissionHealth({ driver, assignedVehicle, activeContract, totalDuration, progressPct, t }) {
  if (!activeContract) return null;
  return (
    <div className="mt-3 rounded-lg border border-money/40 bg-money/5 p-2.5" data-testid={`mission-health-${driver.id}`}>
      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-1.5 text-[10px] uppercase tracking-wider text-money font-mono font-bold">
            <Truck className="w-3 h-3" /> Mission en cours
          </div>
          <p className="text-xs font-semibold text-slate-200 truncate mt-0.5">{activeContract.name}</p>
          <p className="text-[10px] text-slate-500">{assignedVehicle.brand} {assignedVehicle.model}</p>
        </div>
        <div className="text-right flex-shrink-0">
          <p className="text-[10px] text-slate-500">{t('days_remaining_short')}</p>
          <p className="text-base font-black text-money font-mono leading-none" data-testid={`mission-days-${driver.id}`}>
            {activeContract.daysRemaining}<span className="text-[10px] text-slate-500 font-normal">/{totalDuration}j</span>
          </p>
        </div>
      </div>
      <div className="mt-1.5 h-1.5 rounded-full bg-ink-700/50 overflow-hidden" data-testid={`mission-progress-${driver.id}`}
        role="progressbar" aria-valuenow={progressPct} aria-valuemin="0" aria-valuemax="100">
        <div className="h-full bg-money transition-all duration-700" style={{ width: `${progressPct}%` }} />
      </div>
      <div className="flex items-center justify-between mt-1 text-[10px]">
        <span className="text-slate-500">{progressPct}% accompli</span>
        <span className="text-money font-mono font-bold" title="HT — la TVA est facturée en plus">
          {formatMoney(activeContract.dailyRevenue)}/j <span className="text-money/60 text-[10px]">HT</span>
        </span>
      </div>
    </div>
  );
}

// CareerPanel, TrainingPanel, TrainingRow moved to ./CareerPanel.jsx and ./TrainingPanel.jsx — re-exported at top of file.


export function ExpandedDetails({ driver: rawDriver, t }) {
  const driver = localize('driver', rawDriver);
  return (
    <div className="px-4 pb-4 space-y-2">
      <div className="bg-ink-700/30 rounded-lg p-3">
        <div className="grid grid-cols-2 gap-y-2 text-xs">
          <div><span className="text-slate-500">{t('experience')}</span><p className="font-medium text-slate-300">{driver.experience || 0} ans</p></div>
          <div><span className="text-slate-500">{t('accident_risk')}</span><p className="font-medium text-slate-300">{((driver.accidentRisk || 0.1) * 100).toFixed(0)}%</p></div>
          <div><span className="text-slate-500">{t('strength')}</span><p className="font-medium text-money">{driver.strength || '—'}</p></div>
          <div><span className="text-slate-500">{t('weakness')}</span><p className="font-medium text-danger">{driver.weakness || '—'}</p></div>
        </div>
        {(driver.trainings || []).length > 0 && (
          <div className="mt-2 pt-2 border-t border-ink-600">
            <p className="text-[10px] text-slate-500 mb-1 flex items-center gap-1"><Award className="w-3 h-3" /> {t('trainings_done')}</p>
            <div className="flex flex-wrap gap-1">
              {(driver.trainings || []).map((tr) => (
                <span key={tr.type} className="text-[10px] px-1.5 py-0.5 rounded bg-accent/15 text-accent font-medium">{tr.name}</span>
              ))}
            </div>
          </div>
        )}
      </div>
      <PersonalityCursorPanel driver={rawDriver} />
    </div>
  );
}

export function CompetenceLicensesRow({ driver }) {
  return (
    <div className="flex items-center gap-3 mt-2">
      <CompetenceStars value={driver.competence || 2} />
      <div className="flex gap-1">
        {(driver.licenses || ['B']).map(l => (
          <span key={l} className="text-[10px] px-1.5 py-0.5 rounded bg-accent/10 text-accent font-bold">{l}</span>
        ))}
      </div>
    </div>
  );
}

export function AlertsRow({ fatigueAlert, satisfactionAlert, t }) {
  if (!fatigueAlert && !satisfactionAlert) return null;
  return (
    <div className="flex items-center gap-2 mt-2 text-xs text-warn bg-warn/10 rounded px-2 py-1">
      <AlertTriangle className="w-3 h-3 flex-shrink-0" />
      <span>{fatigueAlert ? t('fatigue_high') : ''}{fatigueAlert && satisfactionAlert ? ' — ' : ''}{satisfactionAlert ? t('satisfaction_low') : ''}</span>
    </div>
  );
}

export function AssignedVehicleRow({ driver, assignedVehicle, onUnassign, t }) {
  if (!assignedVehicle) return null;
  return (
    <div className="flex items-center justify-between mt-2">
      <p className="text-xs text-accent flex items-center gap-1">
        <Briefcase className="w-3 h-3" />
        {assignedVehicle.brand} {assignedVehicle.model}
      </p>
      {driver.status !== 'active' || !assignedVehicle.assignedContractId ? (
        <button onClick={() => onUnassign(driver.id)}
          className="text-[10px] text-slate-500 hover:text-danger transition-colors"
          data-testid={`unassign-${driver.id}`}>
          {t('remove')}
        </button>
      ) : null}
    </div>
  );
}
